// Simple helper for querying product inventory through the Shopify Storefront API.
// Usage: include this file in your theme and call ShopifyInventoryClient.fetchProductInventory(handle)
// to retrieve total inventory for a product handle.

(function () {
  const DEFAULT_API_VERSION = "2024-07";

  const config = {
    domain: null,
    token: null,
    apiVersion: DEFAULT_API_VERSION,
  };

  function assertConfigured() {
    if (!config.domain) {
      throw new Error(
        "Shopify domain is not configured. Set ShopifyInventoryClient.domain first."
      );
    }

    if (!config.token) {
      throw new Error(
        "Storefront access token is not configured. Set ShopifyInventoryClient.token first."
      );
    }
  }

  function buildUrl() {
    return `https://${config.domain}/api/${config.apiVersion}/graphql.json`;
  }

  async function fetchProductInventory(productHandle) {
    assertConfigured();

    const query = `#graphql
      query ProductInventory($handle: String!) {
        product(handle: $handle) {
          id
          title
          totalInventory
          variants(first: 100) {
            nodes {
              id
              title
            }
          }
        }
      }
    `;

    const response = await fetch(buildUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": config.token,
      },
      body: JSON.stringify({
        query,
        variables: { handle: productHandle },
      }),
    });

    if (!response.ok) {
      throw new Error(
        `Storefront API request failed with status ${response.status}`
      );
    }

    const { data, errors } = await response.json();

    if (errors) {
      throw new Error(errors.map((error) => error.message).join("; "));
    }

    if (!data || !data.product) {
      throw new Error(`No product found for handle: ${productHandle}`);
    }

    const { product } = data;
    const variants = product.variants?.nodes || [];
    const variantInventory = variants.reduce((total, variant) => {
      const qty =
        typeof variant.inventoryQuantity === "number"
          ? variant.inventoryQuantity
          : 0;
      return total + qty;
    }, 0);

    const totalInventory =
      typeof product.totalInventory === "number"
        ? product.totalInventory
        : variantInventory;

    return {
      id: product.id,
      title: product.title,
      totalInventory,
      variants,
    };
  }

  window.ShopifyInventoryClient = {
    set domain(value) {
      config.domain = value;
    },
    get domain() {
      return config.domain;
    },
    set token(value) {
      config.token = value;
    },
    get token() {
      return config.token;
    },
    set apiVersion(value) {
      config.apiVersion = value || DEFAULT_API_VERSION;
    },
    get apiVersion() {
      return config.apiVersion;
    },
    fetchProductInventory,
  };
})();
